import { apiGet } from "./authDevApiClient.js";

/**
 * @typedef {Object} ModuleCrudAccess
 * @property {boolean} read
 * @property {boolean} create
 * @property {boolean} update
 * @property {boolean} delete
 */

/**
 * @typedef {Object} AdministrationSections
 * @property {boolean} cameraManagement
 * @property {boolean} aiModelsManagement
 * @property {boolean} storageManagement
 * @property {boolean} userManagement
 * @property {boolean} iotDevicesManagement
 * @property {boolean} patrollingRouteManagement
 * @property {boolean} patrolSettings
 * @property {boolean} zoneManagement
 */

/**
 * @typedef {Object} AccountAccess
 * @property {string} userId
 * @property {boolean} isAdmin
 * @property {boolean} isActive
 * @property {boolean} canManageUsers
 * @property {boolean} canManageRoles
 * @property {boolean} canCreateCameras
 * @property {Record<string, ModuleCrudAccess>} modules
 * @property {AdministrationSections | null} administrationSections
 * @property {string[] | null} readableCameraIds
 */

const MODULE_KEYS = [
  "cameras",
  "ai_models",
  "rules",
  "use_cases",
  "zones",
  "users",
  "roles",
  "reports",
];

function normalizeModuleCrud(raw) {
  if (!raw || typeof raw !== "object") {
    return { read: false, create: false, update: false, delete: false };
  }
  const o = raw;
  return {
    read: Boolean(o.read ?? o.canRead ?? o.can_read),
    create: Boolean(o.create ?? o.canCreate ?? o.can_create),
    update: Boolean(o.update ?? o.canUpdate ?? o.can_update),
    delete: Boolean(o.delete ?? o.canDelete ?? o.can_delete),
  };
}

function normalizeModules(raw) {
  const modules = {};
  if (!raw || typeof raw !== "object") return modules;
  for (const key of MODULE_KEYS) {
    if (raw[key] != null) {
      modules[key] = normalizeModuleCrud(raw[key]);
    }
  }
  for (const [key, value] of Object.entries(raw)) {
    if (!modules[key] && value && typeof value === "object") {
      modules[key] = normalizeModuleCrud(value);
    }
  }
  return modules;
}

const ADMIN_SECTION_ALIASES = {
  cameraManagement: "cameraManagement",
  camera_management: "cameraManagement",
  aiModelsManagement: "aiModelsManagement",
  ai_models_management: "aiModelsManagement",
  storageManagement: "storageManagement",
  storage_management: "storageManagement",
  userManagement: "userManagement",
  user_management: "userManagement",
  iotDevicesManagement: "iotDevicesManagement",
  iot_devices_management: "iotDevicesManagement",
  patrollingRouteManagement: "patrollingRouteManagement",
  patrolling_route_management: "patrollingRouteManagement",
  patrolSettings: "patrolSettings",
  patrol_settings: "patrolSettings",
  zoneManagement: "zoneManagement",
  zone_management: "zoneManagement",
};

function normalizeAdministrationSections(raw) {
  if (!raw || typeof raw !== "object") return null;
  const out = {
    cameraManagement: false,
    aiModelsManagement: false,
    storageManagement: false,
    userManagement: false,
    iotDevicesManagement: false,
    patrollingRouteManagement: false,
    patrolSettings: false,
    zoneManagement: false,
  };
  let hasAny = false;
  for (const [key, value] of Object.entries(raw)) {
    const canonical = ADMIN_SECTION_ALIASES[key];
    if (!canonical) continue;
    out[canonical] = Boolean(value);
    hasAny = true;
  }
  return hasAny ? out : null;
}

function unwrapAccessPayload(body) {
  if (body && typeof body === "object" && body.data && typeof body.data === "object") {
    return body.data;
  }
  return body && typeof body === "object" ? body : null;
}

function normalizeAccess(raw) {
  if (!raw || typeof raw !== "object") return null;
  const o = raw;
  const ids = o.readableCameraIds ?? o.readable_camera_ids;
  return {
    userId: String(o.userId ?? o.user_id ?? "").trim(),
    isAdmin: Boolean(o.isAdmin ?? o.is_admin),
    isActive: o.isActive !== false && o.is_active !== false,
    canManageUsers: Boolean(o.canManageUsers ?? o.can_manage_users),
    canManageRoles: Boolean(o.canManageRoles ?? o.can_manage_roles),
    canCreateCameras: Boolean(o.canCreateCameras ?? o.can_create_cameras),
    modules: normalizeModules(o.modules),
    administrationSections: normalizeAdministrationSections(
      o.administrationSections ?? o.administration_sections,
    ),
    readableCameraIds: Array.isArray(ids)
      ? ids.map((id) => String(id))
      : ids === null || ids === undefined
        ? null
        : null,
  };
}

/** GET /api/account/access */
export async function fetchAccountAccess() {
  const body = await apiGet("/api/account/access");
  return normalizeAccess(unwrapAccessPayload(body));
}
